import React, { useState } from 'react';
import axios from 'axios';
import { Flex, Input, IconButton, Image } from '@apia/theme';
import Config from 'config';
import { FaSync, FaVolumeUp } from '@meronex/icons/fa';
import { useApiaComponentContext } from '../apiaComponent/ApiaComponentContext';
import { IApiaCaptcha } from '../../../../types';
import useApiaFieldCommonProps from './useApiaFieldCommonProps';
import usePropsSelector from '../../../../store/propsStore/usePropsSelector';
import MakeField from '../fieldsStore/MakeField';
import { useFieldContext } from '../fieldsStore/FieldContext';
import { getFieldErrorStyles } from '../style';
import useValidateField from '../../../common/previousValidation/useValidateField';
import { formatMessage } from '../../../../utils/util';
import { useAppSelector } from '../../../../hooks/hooks';
import { useApiaFormContext } from '../page/ApiaForm';
import { getLabel } from '@apia/util';

const ApiaCaptcha = MakeField(
  React.memo(
    React.forwardRef<HTMLInputElement>((_, ref) => {
      const { field, fieldId } = useApiaComponentContext<IApiaCaptcha>();
      const props = usePropsSelector(fieldId);

      const { onChange } = useValidateField<string>({
        name: `${window.TAB_ID}${props.formData.frmParent}_${props.formData.frmId}`,
        initialValue: '',
      });

      const {
        setValue,
        state: { value, isValid },
      } = useFieldContext<string>();

      const { attId } = field;

      const [srcURL, setSrcURL] = useState<string>(() => {
        return `${Config.TAB_ID}${
          props?.formData?.id
        }&t=${new Date().getTime()}`;
      });

      const handleSyncClick = async () => {
        // IMG reload
        await axios
          .get(`${Config.CAPTCHA_RELOAD_IMG_URL}${srcURL}`)
          .then(() => {
            // setSrcURL to get the new URL
            if (props?.formData) {
              setSrcURL(
                `${Config.TAB_ID}${
                  props.formData.id
                }&t=${new Date().getTime()}`,
              );
            }
          });
      };

      const isConfirming = useAppSelector(
        (global) => global.confirmFormSlice.confirmIsLoading,
      );
      React.useEffect(() => {
        if (!isConfirming) void handleSyncClick();
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [isConfirming]);

      // IMG URL
      const URL = srcURL !== '' ? `${Config.CAPTCHA_IMG_URL}${srcURL}` : '';

      // HANDLERS
      const handleVoiceClick = async () => {
        const captchaAudio = new Audio(
          `${Config.CAPTCHA_AUDIO_URL}${srcURL}&langCode=${Config.LANG_CODE}`,
        );
        await captchaAudio.play();
      };

      const { identifier } = useApiaFieldCommonProps();

      const { isFormReadOnly } = useApiaFormContext();

      const handleOnChange = React.useCallback(
        (ev: React.ChangeEvent<HTMLInputElement>) => {
          setValue(ev.target.value);
          onChange(ev);
        },
        [onChange, setValue],
      );

      if (isFormReadOnly) return null;

      return (
        <Flex
          key={`captcha_${fieldId}_${attId}`}
          sx={{
            flexDirection: 'column',
          }}
          className="captcha__field"
          {...window.getVariant('layout.execution.form.fields.captcha')}
        >
          <Flex className="captcha__imageContainer">
            <Image src={URL} alt="Img" />
          </Flex>
          <Flex className="captcha__inputContainer">
            <Input
              {...identifier}
              value={value}
              onChange={handleOnChange}
              autoComplete="off"
              ref={ref}
              sx={getFieldErrorStyles(isValid)}
            />

            <Flex className="captcha__buttons">
              <IconButton>
                <FaSync
                  onClick={() => {
                    void handleSyncClick();
                  }}
                  title={getLabel('btnAct').text}
                />
              </IconButton>
              <IconButton>
                <FaVolumeUp
                  onClick={() => {
                    void handleVoiceClick();
                  }}
                  title={getLabel('lblVolExplanation').text}
                />
              </IconButton>
            </Flex>
          </Flex>
        </Flex>
      );
    }),
  ),
  {
    doScriptEvents: () => true,
    doServerEvents: () => true,
    doSynchronize: () => true,
    doValidate: ({
      fieldContext: {
        state: { value },
        setError,
      },
    }) => {
      if (value === '')
        setError(formatMessage(window.MSG_REQ_FIELD, { TOK1: '' }));
      return value !== '';
    },
    preScriptEvents: () => true,
    preServerEvents: () => true,
    preSynchronize: () => true,
  },
);
export default ApiaCaptcha;
